import { useState } from "react";

/**
 * The admin sign in.
 *
 * Every screen used to say "sign in again with an admin address" while no sign
 * in existed anywhere in the console. This is it.
 */
export function AdminSignIn() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  // Why no link went out, when none did. The endpoint answers this the same
  // way for every address, so reading it here cannot narrow the allowlist.
  const [blocked, setBlocked] = useState<string | null>(null);

  async function ask(): Promise<void> {
    if (state === "sending" || email.trim().length === 0) return;
    setState("sending");
    try {
      const response = await fetch("/api/auth", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = (await response.json().catch(() => null)) as
        | { ok?: boolean; sent?: boolean; reason?: string }
        | null;
      if (!response.ok || body?.ok !== true) {
        setState("failed");
        return;
      }
      // A BACKEND THAT SAYS IT SENT NOTHING IS NOT A SUCCESS. The candidate app
      // once showed "check your inbox" over exactly this reply.
      setBlocked(body.sent === false ? (body.reason ?? "unavailable") : null);
      setState("sent");
    } catch {
      setState("failed");
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <span className="font-headline-sm text-headline-sm font-bold tracking-tight text-primary">
            PrepHike
          </span>
          <span className="px-2 py-0.5 rounded bg-primary text-on-primary font-label-sm text-label-sm uppercase tracking-wider font-semibold">
            Admin
          </span>
        </div>

        <h1 className="font-headline-lg text-headline-lg font-bold tracking-tight text-on-surface">
          Sign in to the console
        </h1>
        <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
          A link goes to your address if it is on the admin allowlist. This console reads live
          candidate data, and every record opened is written to the audit ledger.
        </p>

        {state === "sent" && blocked !== null ? (
          <p
            className="mt-6 rounded-xl border border-error bg-error-container p-4 font-body-sm text-body-sm text-on-error-container"
            role="status"
          >
            No link was sent, and none is coming.{" "}
            {blocked === "no_mail_provider"
              ? "This console has no mail provider configured, so it cannot send one."
              : "The console cannot reach its database yet, so it cannot issue one."}{" "}
            This is a deployment problem, not something a second attempt will fix.
          </p>
        ) : state === "sent" ? (
          <p
            className="mt-6 rounded-xl bg-secondary-fixed p-4 font-body-sm text-body-sm text-on-secondary-fixed"
            role="status"
          >
            {/* THE SAME ANSWER EITHER WAY. Saying whether the address is an
                admin would make this endpoint a way to find out. */}
            If that address is on the allowlist, a sign in link is on its way. It works once and
            expires in fifteen minutes.
          </p>
        ) : (
          <div className="mt-6 flex flex-col gap-2">
            <label
              className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant font-semibold"
              htmlFor="admin-email"
            >
              Admin address
            </label>
            <input
              id="admin-email"
              className="min-h-[48px] rounded-xl border border-outline-variant bg-surface-container-lowest px-4 font-body-md text-body-md text-on-surface"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void ask();
              }}
            />
            <button
              className="min-h-[48px] rounded-xl bg-primary text-on-primary font-body-md text-body-md font-semibold disabled:opacity-50"
              type="button"
              disabled={state === "sending" || email.trim().length === 0}
              onClick={() => void ask()}
            >
              {state === "sending" ? "Sending." : "Send a sign in link"}
            </button>
            {state === "failed" ? (
              <p className="font-body-sm text-body-sm text-error" role="alert">
                That did not send. Try again, or check the mail provider is configured.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
